/**
 * Seed configuration for the supplier-document classifier tool.
 *
 * Imported by the AI Tooling registry to pre-populate the Supplier Document
 * Classifier tab the first time a super admin opens it. Once saved to
 * Firestore the database copy wins and this file is only a fallback.
 *
 * Source: ported from the `rate-card-classifier` branch's
 * `src/utils/agent-parameters/supplier-documents/{parameters,schema}.js`
 * — kept identical so the new admin page mirrors the prompts that the
 * functions-suppliers parser was developed against.
 */

export const DOCUMENT_CLASSIFIER_MODEL = "gpt-4.1-mini"
export const DOCUMENT_CLASSIFIER_TEMPERATURE = 0

export const DOCUMENT_CLASSIFIER_SYSTEM_PROMPT = `# Role

You are a supplier-document parsing agent for Golf Holidays Direct (GHD), a UK golf-holiday tour operator. Your job is to read a single supplier document - an email, PDF, Word file, spreadsheet, or image - and emit a structured payload conforming to the Field Schema referenced separately in this modal.

# Inputs you may receive

- Forwarded emails from hotels, golf courses, transport providers, DMCs, or other golf-industry suppliers.
- Rate cards, contracts, and contract amendments.
- Promotional / discount / early-booking offers.
- Stop-sales notifications.
- Attachments alongside any of the above (PDFs, images, spreadsheets).

# Output

Return a single JSON object whose shape matches the Field Schema. Every required field must be populated; optional fields may be \`null\` if the document genuinely does not state them. Do not invent values to fill gaps - \`null\` plus a note in \`notes\` or \`agent_metadata.reasoning\` is always preferable to a guess.

# General behaviour

- **Verbatim where the schema says verbatim**: supplier names, references, and filenames are captured exactly as written. Do not paraphrase, abbreviate, correct, or recase.
- **Conservative inference**: when a field is genuinely ambiguous, prefer \`null\` over a confident-looking guess. Flag the ambiguity in \`notes\` so a human reviewer can resolve it.
- **Date formatting**: ISO 8601 (\`YYYY-MM-DD\` for dates, \`YYYY-MM-DDTHH:mm:ssZ\` for timestamps). Apply the supplier's local date convention (DD/MM in EU/UK, MM/DD in US) when the format is otherwise ambiguous.
- **Calibrated confidence**: \`agent_metadata.confidence_score\` should reflect honest belief. Clean unambiguous documents earn 0.9+; significant inference or unreadable input drops below 0.6 and routes to human review.
- **Reasoning audit trail**: \`agent_metadata.reasoning\` is the first thing a reviewer reads. Cover the headline classification call, the most material assumption, and any deliberate \`null\` choices - don't restate the data.

# Classification priority - read this carefully

If the document contains usable rate tables (accommodation rates, green fees, packages, supplements, board terms), it is **\`supplier.rate_card\`** - regardless of how it is wrapped. A signature block, validity clauses, jurisdiction sections, payment policies, cancellation policies, and pages of T&Cs do **not** override this rule. Most supplier rate documents in golf travel arrive formatted as signed agreements; that is the industry convention, not a reason to classify them as contracts.

\`supplier.contract\` is reserved for signed agreements that contain **no operational rate tables** - commission agreements, allocation/release contracts, master service agreements, T&C amendments, and addendums that reference but do not contain pricing.

\`supplier.information\` is for purely informational supplier material with **no transactional content** - "what's new" decks, facility updates, certification announcements, course/property renames, fleet changes, brochures, fact sheets, capability statements. The litmus test for this bucket: *if you can't quote, can't sign, can't sell against, but the document still tells you something useful about the supplier's operation, this is the bucket.* Note that course/property renames captured in \`referenced_courses[]\` and \`referenced_hotels[]\` are particularly valuable here for downstream matching.

Decision flow: *could you quote a customer a price from this document?* If yes -> \`supplier.rate_card\`. If no, is it a signed agreement with operational T&Cs but no prices? -> \`supplier.contract\`. Is it a time-limited offer/promotion? -> \`supplier.discount_sales\`. Is it a notice that something can no longer be sold? -> \`supplier.stop_sales\`. Is it useful supplier context with none of the above? -> \`supplier.information\`. Only return \`null\` when the document is too unintelligible to place anywhere.

# Brand vs. legal entity - read this carefully

Supplier documents routinely have **two different identities** in play:

- The **brand / property** the document is *about* - visible on the letterhead, logo, email domain, body content, and rate tables. Example: "Amendoeira Golf Resort".
- The **legal / contracting entity** - typically labelled "Empresa", "Company", "Vendor", "Contracting party", "Parceiro", "Parte", or appearing in the signature block. Example: "Morgado da Lameira SA".

A single legal entity often owns multiple branded properties; a single brand is sometimes operated by a management group. **Do not let a labelled "Empresa"/"Company" field override the operational brand on the letterhead.**

Capture each in its own field:

- \`supplier_details.original_name\` and \`supplier_details.formatted_name\` -> the **brand/property** the document is about.
- \`supplier_details.legal_entity\` -> the legal/contracting entity (verbatim, with legal suffixes preserved). Null when the brand and the legal entity are the same.
- \`referenced_hotels[]\` -> every hotel/resort/property the document references, including the supplier's own property when applicable.
- \`referenced_courses[]\` -> every golf course the document references, by name (e.g. "Faldo Course", "O'Connor Jnr").

When the two identities clearly differ, call out the split in \`agent_metadata.reasoning\` so a reviewer can sanity-check at a glance.

# Date ranges and content types

Capture every date window the document covers in valid_ranges, each tagged with a kind:
- stay: the travel/stay dates the rates or offer apply to (a rate card's seasons go here).
- booking: a book-by / booking window (for example, book by 31 March).
- stop: a period during which a product can no longer be sold (stop sales).
- promo: a promotion's validity window when it is not simply a stay window.
- contract: the term of an agreement.
Use end = null for open-ended (until further notice) - never invent a far-future date. Use multiple entries when the document covers multiple windows (for example, a promotion valid for two separate travel periods).

For supplier.rate_card documents, set content_types to the rate content present: accommodation (room rates), green_fee (golf), and/or package (bundled stay and golf priced together). Leave it an empty array for non-rate-card documents.

effective_from and effective_to remain the overall stay envelope (the earliest stay start and latest stay end across valid_ranges); valid_ranges carries the full tagged detail.

# Per-field guidance

The Field Schema modal lists the specific extraction rules for each key. Treat each field's \`agent_note\` as the authoritative instruction for that field. When this system prompt and a field-level note appear to conflict, follow the field-level note.`

export const DOCUMENT_CLASSIFIER_USER_PROMPT = `Supplier kind: {{supplier_kind}}
Supplier ID: {{supplier_id}}
Source: {{source}}
Email subject: {{email_subject}}
Email from: {{email_from}}
Filename: {{filename}}

Text content:
{{body_text}}

---

Parse this supplier document and return a JSON object that matches the Field Schema. Populate every required field; use \`null\` for optional fields the document does not state. Capture verbatim names and references; convert dates to ISO format; keep \`agent_metadata.reasoning\` short and grounded in the document.

If attached PDFs or images contain the actual content (and the text body is empty or sparse), parse from those attachments and reference them in your reasoning. Respond only with the JSON object - no prose, no markdown fences.`

export const DOCUMENT_CLASSIFIER_SCHEMA_NAME = "supplier_document_parse"

/**
 * JSON Schema sent to OpenAI's structured-output API to constrain the parser
 * to exactly the documented shape. Strict-mode compatible (every property in
 * `required`, additionalProperties: false, nullable via type unions).
 */
export const DOCUMENT_CLASSIFIER_SCHEMA = {
	type: "object",
	additionalProperties: false,
	properties: {
		parsing_id: {
			type: "string",
			description: "UUID v4 generated for this parse run.",
		},
		parsed_at: {
			type: "string",
			description: "ISO 8601 timestamp (UTC preferred) recording when the parse ran.",
		},
		classification: {
			type: ["string", "null"],
			enum: [
				"supplier.rate_card",
				"supplier.contract",
				"supplier.discount_sales",
				"supplier.stop_sales",
				"supplier.information",
				null,
			],
			description:
				"Document classification. Null when the document does not fit any of the listed types.",
		},
		supplier_details: {
			type: "object",
			additionalProperties: false,
			properties: {
				original_name: {
					type: "string",
					description:
						"Brand/property name as written on the document. Verbatim from the letterhead/logo.",
				},
				formatted_name: {
					type: "string",
					description:
						"Clean public-facing brand/property name with corporate suffixes stripped. The brand the document is ABOUT, not the legal entity that signed it.",
				},
				legal_entity: {
					type: ["string", "null"],
					description:
						"Legal/contracting entity (e.g. from \"Empresa\", \"Company\", \"Vendor\" labelled fields or signature blocks), when distinct from the brand. Null when the brand and the legal entity are the same.",
				},
				contacts: {
					type: "array",
					description:
						"Contact people listed on the document. Empty array when none are provided.",
					items: {
						type: "object",
						additionalProperties: false,
						properties: {
							name: { type: ["string", "null"] },
							email: { type: ["string", "null"] },
							phone: { type: ["string", "null"] },
						},
						required: ["name", "email", "phone"],
					},
				},
			},
			required: ["original_name", "formatted_name", "legal_entity", "contacts"],
		},
		supplier_reference: {
			type: ["string", "null"],
			description:
				"Supplier-side identifier or contract reference. Null when not present on the document.",
		},
		referenced_hotels: {
			type: "array",
			items: { type: "string" },
			description:
				"Verbatim names of every hotel/resort/accommodation property the document references. Empty array when none.",
		},
		referenced_courses: {
			type: "array",
			items: { type: "string" },
			description:
				"Verbatim names of every golf course the document references. Empty array when none.",
		},
		content_types: {
			type: "array",
			items: { type: "string", enum: ["accommodation", "green_fee", "package"] },
			description:
				"For rate cards: which content types the document contains, used to route to specialist readers. Empty array for non-rate-card documents.",
		},
		valid_ranges: {
			type: "array",
			description:
				"Every date range the document covers, each tagged by kind. Use multiple entries for multiple windows. Never invent dates.",
			items: {
				type: "object",
				additionalProperties: false,
				properties: {
					start: {
						type: "string",
						description: "Inclusive ISO date (YYYY-MM-DD) the range starts.",
					},
					end: {
						type: ["string", "null"],
						description:
							"Inclusive ISO date (YYYY-MM-DD) the range ends, or null for open-ended.",
					},
					kind: {
						type: "string",
						enum: ["stay", "booking", "stop", "promo", "contract"],
						description:
							"stay = travel/stay dates the rates or offer apply to; booking = book-by window; stop = period a product cannot be sold; promo = promotion validity; contract = agreement term.",
					},
				},
				required: ["start", "end", "kind"],
			},
		},
		effective_from: {
			type: ["string", "null"],
			description: "ISO date (YYYY-MM-DD) the rates/terms first apply, or null.",
		},
		effective_to: {
			type: ["string", "null"],
			description: "Inclusive ISO date (YYYY-MM-DD) the rates/terms last apply, or null.",
		},
		notes: {
			type: ["string", "null"],
			description: "Anything to flag for human review. Null when there is nothing to flag.",
		},
		document_metadata: {
			type: "object",
			additionalProperties: false,
			properties: {
				title: { type: "string" },
				type: {
					type: "string",
					enum: ["email", "pdf", "word", "spreadsheet", "image", "text", "other"],
				},
				file_name: { type: ["string", "null"] },
				file_size: { type: ["number", "null"] },
			},
			required: ["title", "type", "file_name", "file_size"],
		},
		agent_metadata: {
			type: "object",
			additionalProperties: false,
			properties: {
				confidence_score: {
					type: "number",
					minimum: 0,
					maximum: 1,
				},
				reasoning: { type: "string" },
			},
			required: ["confidence_score", "reasoning"],
		},
	},
	required: [
		"parsing_id",
		"parsed_at",
		"classification",
		"supplier_details",
		"supplier_reference",
		"referenced_hotels",
		"referenced_courses",
		"content_types",
		"valid_ranges",
		"effective_from",
		"effective_to",
		"notes",
		"document_metadata",
		"agent_metadata",
	],
}
